/**
 * Kanban board (T-037).
 *
 * Three columns: Backlog / Active / Delivered (PR-08, US-07). Backlog is
 * drag-reorderable (writes `order_in_backlog`); Active and Delivered are not
 * reorderable. The active count is shown against the tier limit (1 Studio /
 * 2 Pro) per PR-13.
 *
 * Persistence interpretation (documented for the validator): the T-037 AC
 * names "PATCH /api/tasks/:id/order". No such server endpoint exists in V1
 * (the server surface is consume-only for Phase G and ships no task-order
 * route). The migration-0007 `tasks_subscriber_update` RLS policy explicitly
 * permits a subscriber to UPDATE a row while it stays in `status='backlog'`,
 * which is exactly a reorder. So the reorder is persisted directly via
 * supabase-js per ARCH-001 (no ORM) and RLS-enforced ownership, not via a
 * dedicated endpoint. The behavior the AC describes (drag reorder persists)
 * is met; the transport differs. Flagged in IMPL-NOTES for T-037.
 *
 * Drag-and-drop uses native HTML5 drag events — no DnD library is added (no
 * new dependency, consistent with the frozen package set). Keyboard-operable
 * "Move up / Move down" controls accompany each backlog card so reorder is
 * not mouse-only (design-guidelines §6 keyboard nav).
 */

import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react';
import type { Task } from '@studio/api-schemas';
import { supabase } from '@/lib/supabase';
import { kanbanColumnForStatus } from '@/lib/portal';
import { toast } from '@/components/ui/toast';
import { StatusPill } from './StatusPill';
import { TASK_TYPE_LABEL } from './task-type-labels';

interface KanbanProps {
  tasks: Task[];
  activeSlots: number;
}

export function Kanban({ tasks, activeSlots }: KanbanProps) {
  const initial = useMemo(() => bucketize(tasks), [tasks]);
  const [backlog, setBacklog] = useState<Task[]>(initial.backlog);
  const active = initial.active;
  const delivered = initial.delivered;

  async function persistOrder(ordered: Task[]) {
    // Write the new 0-based order_in_backlog for each row. RLS permits this
    // (status stays 'backlog'). Best-effort: failures roll the UI back.
    const updates = ordered.map((t, idx) =>
      supabase
        .from('tasks')
        .update({ order_in_backlog: idx })
        .eq('id', t.id),
    );
    const results = await Promise.all(updates);
    const failed = results.find((r) => r.error);
    if (failed?.error) {
      toast.error('Could not save the new order. Refresh and try again.');
      setBacklog(initial.backlog);
      return;
    }
  }

  function reorder(from: number, to: number) {
    if (to < 0 || to >= backlog.length || from === to) return;
    const next = backlog.slice();
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;
    next.splice(to, 0, moved);
    setBacklog(next);
    void persistOrder(next);
  }

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      <KanbanColumn
        title="Backlog"
        count={backlog.length}
        emptyState={
          <EmptyBacklog />
        }
      >
        {backlog.map((task, idx) => (
          <BacklogCard
            key={task.id}
            task={task}
            index={idx}
            total={backlog.length}
            onMoveUp={() => reorder(idx, idx - 1)}
            onMoveDown={() => reorder(idx, idx + 1)}
            onDropReorder={(fromIdx) => reorder(fromIdx, idx)}
          />
        ))}
      </KanbanColumn>

      <KanbanColumn
        title="Active"
        count={active.length}
        subtitle={`${active.length} of ${activeSlots} slot${activeSlots === 1 ? '' : 's'} in motion`}
        emptyState={
          <p className="text-sm text-[color:var(--color-text-mute)]">
            Nothing in motion right now. The next backlog task gets pulled on
            the cycle.
          </p>
        }
      >
        {active.map((task) => (
          <StaticCard key={task.id} task={task} kind="active" />
        ))}
      </KanbanColumn>

      <KanbanColumn
        title="Delivered"
        count={delivered.length}
        emptyState={
          <p className="text-sm text-[color:var(--color-text-mute)]">
            Delivered work lands here. Nothing yet.
          </p>
        }
      >
        {delivered.map((task) => (
          <StaticCard key={task.id} task={task} kind="delivered" />
        ))}
      </KanbanColumn>
    </div>
  );
}

function bucketize(tasks: Task[]): {
  backlog: Task[];
  active: Task[];
  delivered: Task[];
} {
  const backlog: Task[] = [];
  const active: Task[] = [];
  const delivered: Task[] = [];
  for (const t of tasks) {
    const col = kanbanColumnForStatus(t.status);
    if (col === 'backlog') backlog.push(t);
    else if (col === 'active') active.push(t);
    else if (col === 'delivered') delivered.push(t);
    // 'hidden' (split parents) intentionally dropped.
  }
  backlog.sort((a, b) => {
    const ao = a.order_in_backlog ?? Number.MAX_SAFE_INTEGER;
    const bo = b.order_in_backlog ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return a.created_at.localeCompare(b.created_at);
  });
  delivered.sort((a, b) =>
    (b.delivered_at ?? b.updated_at).localeCompare(
      a.delivered_at ?? a.updated_at,
    ),
  );
  return { backlog, active, delivered };
}

/**
 * Column kind, used only to tune the card accent (type-pill color + card
 * border) to match the prototype TCard treatment. Pure presentation; the
 * bucketing/data wiring is unchanged.
 */
type ColumnKind = 'backlog' | 'active' | 'delivered';

function KanbanColumn({
  title,
  count,
  subtitle,
  emptyState,
  children,
}: {
  title: string;
  count: number;
  subtitle?: string;
  emptyState: React.ReactNode;
  children: React.ReactNode;
}) {
  const isEmpty = count === 0;
  return (
    <section className="flex flex-col" aria-label={`${title} column`}>
      <header className="mb-2.5 flex items-center gap-2">
        <h2 className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
          {title}
        </h2>
        <span className="inline-flex items-center rounded-full border border-[color:var(--color-gray-100)] bg-[color:var(--color-white)] px-[7px] py-px text-[10px] font-semibold leading-none text-[color:var(--color-black)]">
          {count}
        </span>
      </header>
      {subtitle ? (
        <p className="mb-2.5 text-xs text-[color:var(--color-text-soft)]">
          {subtitle}
        </p>
      ) : null}
      <div className="space-y-2">{isEmpty ? emptyState : children}</div>
    </section>
  );
}

/** Type pill colored by column state, mirroring the prototype TCard Tag. */
function TypeTag({ task, kind }: { task: Task; kind: ColumnKind }) {
  const variantClass =
    kind === 'active'
      ? 'bg-[color:var(--color-green-accent-subtle)] text-[color:var(--color-green-accent)] border border-[color:var(--color-green-accent)]'
      : kind === 'delivered'
        ? 'bg-[color:var(--color-rose)] text-[color:var(--color-navy)]'
        : 'bg-[color:var(--color-linen)] text-[color:var(--color-black)] border border-[color:var(--color-line-dark)]';
  return (
    <span
      className={[
        'inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase leading-none tracking-[0.05em]',
        variantClass,
      ].join(' ')}
    >
      {TASK_TYPE_LABEL[task.type]}
    </span>
  );
}

function CardShell({
  task,
  kind,
  children,
}: {
  task: Task;
  kind: ColumnKind;
  children?: React.ReactNode;
}) {
  const borderClass =
    kind === 'active'
      ? 'border-[color:var(--color-navy)]'
      : kind === 'delivered'
        ? 'border-[color:var(--color-rose)]'
        : 'border-[color:var(--color-gray-100)]';
  return (
    <Link
      to="/portal/tasks/$taskId"
      params={{ taskId: task.id }}
      className={[
        'block rounded-md border-[1.5px] bg-[color:var(--color-white)] p-[11px_13px] shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]',
        borderClass,
      ].join(' ')}
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <TypeTag task={task} kind={kind} />
        <span className="ml-auto text-[10px] text-[color:var(--color-gray-300)]">
          {task.id.slice(0, 8)}
        </span>
      </div>
      <p className="mb-1.5 line-clamp-3 text-[13px] font-medium leading-snug text-[color:var(--color-black)]">
        {task.brief}
      </p>
      <StatusPill status={task.status} />
      {children}
    </Link>
  );
}

function StaticCard({ task, kind }: { task: Task; kind: ColumnKind }) {
  return <CardShell task={task} kind={kind} />;
}

function BacklogCard({
  task,
  index,
  total,
  onMoveUp,
  onMoveDown,
  onDropReorder,
}: {
  task: Task;
  index: number;
  total: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDropReorder: (fromIndex: number) => void;
}) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer.getData('text/plain'));
        if (!Number.isNaN(from)) onDropReorder(from);
      }}
      className="rounded-md border-[1.5px] border-[color:var(--color-gray-100)] bg-[color:var(--color-white)] shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]"
    >
      <div className="flex items-start gap-2 p-[11px_13px]">
        <span
          className="mt-0.5 cursor-grab text-[color:var(--color-text-mute)]"
          aria-hidden="true"
        >
          <GripVertical size={16} strokeWidth={1.5} />
        </span>
        <Link
          to="/portal/tasks/$taskId"
          params={{ taskId: task.id }}
          className="min-w-0 flex-1"
        >
          <div className="mb-1.5 flex items-center gap-1.5">
            <span className="inline-flex items-center rounded-sm border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] px-2 py-0.5 text-[10px] font-medium uppercase leading-none tracking-[0.05em] text-[color:var(--color-black)]">
              {TASK_TYPE_LABEL[task.type]}
            </span>
            <span className="ml-auto text-[10px] text-[color:var(--color-gray-300)]">
              {task.id.slice(0, 8)}
            </span>
          </div>
          <p className="mb-1.5 line-clamp-3 text-[13px] font-medium leading-snug text-[color:var(--color-black)]">
            {task.brief}
          </p>
          <StatusPill status={task.status} />
        </Link>
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            aria-label={`Move "${task.brief.slice(0, 40)}" up`}
            className="rounded-sm p-1 text-[color:var(--color-text-mute)] hover:bg-[color:var(--color-linen)] disabled:opacity-30"
          >
            <ArrowUp size={14} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === total - 1}
            aria-label={`Move "${task.brief.slice(0, 40)}" down`}
            className="rounded-sm p-1 text-[color:var(--color-text-mute)] hover:bg-[color:var(--color-linen)] disabled:opacity-30"
          >
            <ArrowDown size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyBacklog() {
  return (
    <div className="rounded-md border border-dashed border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-6 text-center">
      <p className="mb-3 text-sm text-[color:var(--color-text-mute)]">
        Your backlog is empty. The work starts when you add the first task.
      </p>
      <Link
        to="/portal/tasks/new"
        className="inline-flex items-center justify-center rounded-md bg-[color:var(--color-navy)] px-[22px] py-2.5 text-sm font-medium text-[color:var(--color-linen)] transition-colors hover:bg-[color:var(--color-navy-hover)]"
      >
        + Add your first task
      </Link>
    </div>
  );
}
